/**
 * PeriodPicker — chip-row для выбора периода + кастомный date range.
 * Используется на каждой странице чтобы фильтровать данные по периоду.
 *
 * Связан с глобальным стейтом @/state/period — все страницы видят одни
 * и те же даты при переходе между разделами.
 *
 * Кастомный период: даты редактируются в локальном state поповера и
 * применяются ТОЛЬКО по кнопке «Применить». Это нужно чтобы во время
 * набора даты не уходили запросы с полу-валидным значением (HTTP 422).
 */
import { useEffect, useState } from "react";
import { Icon } from "@/components/prototype/Icon";
import { usePeriod, type PeriodPreset } from "@/state/period";

/* «7 дней» / «30 дней» — скользящие окна до сегодня; «Прошлая неделя» /
   «Прошлый месяц» — календарные (пн–вс, 1-е — последнее число). */
const PRESETS: [PeriodPreset, string][] = [
  ["today",      "Сегодня"],
  ["week",       "7 дней"],
  ["prev_week",  "Прошлая неделя"],
  ["month",      "30 дней"],
  ["this_month", "Этот месяц"],
  ["prev_month", "Прошлый месяц"],
  ["quarter",    "Квартал"],
  ["year",       "365 дней"],
  ["ytd",        "Этот год"],
];

const ISO_RE = /^\d{4}-\d{2}-\d{2}$/;
function isValidDate(s: string): boolean {
  if (!ISO_RE.test(s)) return false;
  const d = new Date(s + "T00:00:00");
  return !isNaN(d.getTime());
}

export function PeriodPicker() {
  const { from, to, preset, setPreset, setRange } = usePeriod();
  const [open, setOpen] = useState(false);
  const [draftFrom, setDraftFrom] = useState(from);
  const [draftTo, setDraftTo] = useState(to);

  // При открытии поповера — синхронизируем драфт с текущим глобальным значением.
  useEffect(() => {
    if (open) {
      setDraftFrom(from);
      setDraftTo(to);
    }
  }, [open, from, to]);

  const draftValid = isValidDate(draftFrom) && isValidDate(draftTo) && draftFrom <= draftTo;
  const draftChanged = draftFrom !== from || draftTo !== to || preset !== "custom";

  const apply = () => {
    if (!draftValid) return;
    setRange(draftFrom, draftTo);
    setOpen(false);
  };

  return (
    <div className="chip-row" style={{ position: "relative" }}>
      {PRESETS.map(([p, label]) => (
        <button
          key={p}
          className={`chip ${preset === p ? "active" : ""}`}
          onClick={() => { setPreset(p); setOpen(false); }}
        >
          {label}
        </button>
      ))}
      <button
        className={`chip ${preset === "custom" ? "active" : ""}`}
        onClick={() => setOpen(o => !o)}
        title="Произвольный период"
      >
        <Icon name="calendar" size={14}/>
        &nbsp;{preset === "custom" ? `${from.slice(5)} — ${to.slice(5)}` : "Период"}
      </button>

      {open && (
        <div style={{
          position: "absolute", top: "100%", right: 0, marginTop: 8,
          background: "var(--card)", border: "1px solid var(--line)",
          borderRadius: "var(--radius-lg)", padding: 16, zIndex: 50,
          display: "flex", flexDirection: "column", gap: 12, minWidth: 280,
          boxShadow: "0 8px 24px rgba(0,0,0,0.08)",
        }}>
          <div style={{ fontSize: 11, letterSpacing: "0.1em", textTransform: "uppercase", color: "var(--mute)" }}>
            Кастомный период
          </div>
          <label style={{ display: "flex", flexDirection: "column", gap: 4, fontSize: 12, color: "var(--ink-3)" }}>
            С
            <input
              className="input"
              type="date"
              value={draftFrom}
              onChange={(e) => setDraftFrom(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter" && draftValid) apply(); }}
            />
          </label>
          <label style={{ display: "flex", flexDirection: "column", gap: 4, fontSize: 12, color: "var(--ink-3)" }}>
            По
            <input
              className="input"
              type="date"
              value={draftTo}
              onChange={(e) => setDraftTo(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter" && draftValid) apply(); }}
            />
          </label>
          {!draftValid && (draftFrom || draftTo) && (
            <div style={{ fontSize: 11, color: "var(--brick)" }}>
              {draftFrom && draftTo && draftFrom > draftTo
                ? "Дата «С» позже даты «По»"
                : "Введите обе даты в формате ГГГГ-ММ-ДД"}
            </div>
          )}
          <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
            <button className="btn btn-sm" onClick={() => setOpen(false)}>Отмена</button>
            <button
              className="btn btn-sm btn-primary"
              onClick={apply}
              disabled={!draftValid || !draftChanged}
            >
              Применить
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
